import { FormattedMessage, useIntl } from "react-intl";
import { Dropdown } from "react-bootstrap";
import { toast } from "react-toastify";
import { FC, useState } from "react";

import { ORDER_STATUS_TRANSLATIONS } from "../../../../helpers/constants";
import {
	useReturnFulfillmentMutation,
	useShipFulfillmentMutation,
} from "../../../../api/ordersAPI";
import {
	TGetOrderResult,
	TOrderFulfillment,
	TTenant,
} from "../../../../api/api.types";

import EditTikTokFulfillmentTrackingNumber from "../../../modals/EditFulfillmentTrackingNumber/EditTikTokFulfillmentTrackingNumber";
import EditFulfillmentTrackingNumber from "../../../modals/EditFulfillmentTrackingNumber/EditFulfillmentTrackingNumber";
import EditFulfillmentShippingCost from "../../../modals/EditFulfillmentShippingCost/EditFulfillmentShippingCost";
import EditFulfillmentStatus from "../../../modals/EditFulfillmentStatus/EditFulfillmentStatus";

import Arrow from "../../../assets/images/arrow.svg";

type TOrderFulfillmentBlockProps = {
	fulfillment: TOrderFulfillment;
	tenantData: TTenant | undefined;
	orderData: TGetOrderResult;
};

const OrderFulfillmentBlock: FC<TOrderFulfillmentBlockProps> = ({
	fulfillment,
	tenantData,
	orderData,
}) => {
	const [showTrackingNumberEdit, setShowTrackingNumberEdit] = useState(false);
	const [showShippingCostEdit, setShowShippingCostEdit] = useState(false);
	const [showStatusEdit, setShowStatusEdit] = useState(false);

	const intl = useIntl();

	const [returnFulfillmentOrder] = useReturnFulfillmentMutation();
	const [shipFulfillment] = useShipFulfillmentMutation();

	const handleUpdateFulfillmentStatus = () => {
		setShowStatusEdit(true);
	};

	const handleUpdateFulfillmentTrackingNumber = () => {
		setShowTrackingNumberEdit(true);
	};

	const handleUpdateFulfillmentShippingCost = () => {
		setShowShippingCostEdit(true);
	};

	const handleReturnFulfillment = () => {
		returnFulfillmentOrder({
			id: orderData.id,
			item_ids: [fulfillment.id],
		}).then((response) => {
			if ("error" in response) {
				const error = (response.error as { data: { error: string[] } })
					?.data?.error?.[0];

				if (error) {
					toast.error(error);
				} else {
					toast.error(
						intl.formatMessage({
							id: "anErrorOccurredWhileReturningTheFulfillment",
						})
					);
				}
			} else {
				toast.success(
					intl.formatMessage({
						id: "fulfillmentReturnRequestHasBeenSentSuccessfully",
					})
				);
			}
		});
	};

	const handleShipFulfillment = () => {
		shipFulfillment(fulfillment.id).then((response) => {
			if ("error" in response) {
				const error = (response.error as { data: { error: string[] } })
					?.data?.error?.[0];

				if (error) {
					toast.error(error);
				} else {
					toast.error(<FormattedMessage id="fulfillmentShipError" />);
				}
			} else {
				toast.success(<FormattedMessage id="fulfillmentShipped" />);
			}
		});
	};

	return (
		<div
			data-testid="fulfillments-block"
			className="order-details__fulfillment-details-block"
		>
			<div className="order-details__choose-action">
				<p className="order-details__fulfillment-details-title">
					<FormattedMessage id="fulfillmentWithDots" />{" "}
					{
						fulfillment?.amazon_fulfillment_shipments[0]
							?.amazon_shipment_id
					}
				</p>
				<div className="d-flex align-items-center gap-3">
					<div className="order-details__order-status">
						<div
							className={`order-details__order-status-indicator ${fulfillment.status === "Error" ? "error" : ""}`}
						></div>
						<span className="order-details__order-status-text">
							<FormattedMessage
								id={
									ORDER_STATUS_TRANSLATIONS[
										(fulfillment.status ??
											"New") as keyof typeof ORDER_STATUS_TRANSLATIONS
									] || fulfillment.status
								}
							/>
						</span>
					</div>
					{orderData?.seller_integration?.partner ===
						"Shopify Integration" &&
						fulfillment.vendor_integration &&
						fulfillment.vendor_tenant === tenantData?.id &&
						fulfillment.status === "Completed" && (
							<Dropdown className="d-flex justify-content-center">
								<Dropdown.Toggle className="user-header-dropdown orders__dropdown-toggle p-0">
									<div className="orders__dropdown-button">
										<FormattedMessage id="action" />
										<img
											className="arrow-icon"
											src={Arrow}
											alt=""
										/>
									</div>
								</Dropdown.Toggle>
								<Dropdown.Menu className="orders__dropdown-menu-wrapper">
									<Dropdown.Item
										onClick={() =>
											handleReturnFulfillment()
										}
										className="orders__dropdown-item"
									>
										<FormattedMessage id="returnFulfillment" />
									</Dropdown.Item>
								</Dropdown.Menu>
							</Dropdown>
						)}
					{fulfillment.vendor_integration === null &&
						fulfillment.vendor_tenant === tenantData?.id && (
							<Dropdown className="d-flex justify-content-center">
								<Dropdown.Toggle className="user-header-dropdown orders__dropdown-toggle p-0">
									<div className="orders__dropdown-button">
										<FormattedMessage id="action" />
										<img
											className="arrow-icon"
											src={Arrow}
											alt=""
										/>
									</div>
								</Dropdown.Toggle>
								<Dropdown.Menu className="orders__dropdown-menu-wrapper">
									<div className="orders__dropdown-menu">
										{orderData?.seller_integration
											?.partner ===
										"TikTok Integration" ? (
											<Dropdown.Item
												onClick={handleShipFulfillment}
												className="orders__dropdown-item"
											>
												<FormattedMessage id="shipFulfillment" />
											</Dropdown.Item>
										) : (
											<Dropdown.Item
												onClick={() =>
													handleUpdateFulfillmentStatus()
												}
												className="orders__dropdown-item"
											>
												<FormattedMessage id="updateStatus" />
											</Dropdown.Item>
										)}
										<Dropdown.Item
											onClick={() =>
												handleUpdateFulfillmentTrackingNumber()
											}
											className="orders__dropdown-item"
										>
											<FormattedMessage id="updateTrackingNumber" />
										</Dropdown.Item>
										<Dropdown.Item
											onClick={() =>
												handleUpdateFulfillmentShippingCost()
											}
											className="orders__dropdown-item"
										>
											<FormattedMessage id="updateShippingCost" />
										</Dropdown.Item>
									</div>
								</Dropdown.Menu>
							</Dropdown>
						)}
				</div>
			</div>
			<div className="order-details__fulfillment-details-table-wrapper">
				<table className="table mb-0">
					<thead className="thead-light">
						<tr>
							<th className="text-center vertical-align-middle">
								<FormattedMessage id="image" />
							</th>
							<th className="text-center vertical-align-middle">
								<FormattedMessage id="productName" />
							</th>
							<th className="text-center vertical-align-middle">
								<FormattedMessage id="moreInformation" />
							</th>
							<th className="text-center vertical-align-middle">
								<FormattedMessage id="quantity" />
							</th>
							<th className="text-center vertical-align-middle">
								<FormattedMessage id="price" />
							</th>
						</tr>
					</thead>
					<tbody>
						{fulfillment?.fulfillment_line_items &&
							fulfillment?.fulfillment_line_items.length > 0 &&
							fulfillment?.fulfillment_line_items.map(
								(fulfillmentLineItem) => (
									<tr key={fulfillmentLineItem.id}>
										<td className="text-center">
											<img
												height={70}
												src={
													fulfillmentLineItem
														?.variant_product
														?.images[0]?.url
														? fulfillmentLineItem
																?.variant_product
																?.images[0]?.url
														: fulfillmentLineItem
																?.variant_product
																?.images[0]
																?.image
												}
												alt=""
											/>
										</td>

										<td
											width="30%"
											className="text-center max-width-td"
										>
											<div className="d-flex flex-column">
												<p className="wrapped-table-row">
													{
														fulfillmentLineItem
															?.variant_product
															?.title
													}
												</p>
												<p className="">
													SKU:{" "}
													{
														fulfillmentLineItem
															?.variant_product
															?.sku
													}
												</p>
											</div>
										</td>

										<td
											width="30%"
											className="text-center max-width-td"
										>
											<div className="d-flex flex-column">
												{fulfillment?.tracking_info
													?.carrier_name && (
													<p className="order-details__fulfillment-two-rows">
														<FormattedMessage id="carrierWithDots" />{" "}
														<span>
															{
																fulfillment
																	?.tracking_info
																	?.carrier_name
															}
														</span>
													</p>
												)}
												{fulfillmentLineItem
													?.amazon_fulfillment_shipment_items
													.length > 0 && (
													<p className="order-details__fulfillment-two-rows">
														<FormattedMessage id="packingNumberWithDots" />{" "}
														<span>
															{
																fulfillmentLineItem
																	?.amazon_fulfillment_shipment_items[0]
																	?.package_number
															}
														</span>
													</p>
												)}
												<p className="order-details__fulfillment-two-rows">
													<FormattedMessage id="trackingNumberWithDots" />{" "}
													<span>
														{fulfillment.vendor_integration ===
															null &&
														fulfillment.vendor_tenant ===
															tenantData?.id
															? fulfillment
																	.tracking_info
																	?.tracking_number
															: fulfillmentLineItem
																	?.amazon_fulfillment_shipment_items[0]
																	?.amazon_fulfillment_shipment_package
																	.tracking_number}
													</span>
												</p>
											</div>
										</td>
										<td className="text-center order-details-info-text">
											{fulfillmentLineItem?.quantity}
										</td>
										<td className="text-center order-details-info-text">
											${fulfillmentLineItem?.price / 100}
										</td>
									</tr>
								)
							)}
					</tbody>
				</table>
			</div>
			{showStatusEdit && (
				<EditFulfillmentStatus
					fulfillment={fulfillment}
					onHide={() => setShowStatusEdit(false)}
				/>
			)}
			{showTrackingNumberEdit && (
				<>
					{orderData?.seller_integration?.partner ===
					"TikTok Integration" ? (
						<EditTikTokFulfillmentTrackingNumber
							fulfillment={fulfillment}
							onHide={() => setShowTrackingNumberEdit(false)}
							orderId={orderData?.id}
						/>
					) : (
						<EditFulfillmentTrackingNumber
							fulfillment={fulfillment}
							onHide={() => setShowTrackingNumberEdit(false)}
						/>
					)}
				</>
			)}
			{showShippingCostEdit && (
				<EditFulfillmentShippingCost
					fulfillment={fulfillment}
					onHide={() => setShowShippingCostEdit(false)}
				/>
			)}
		</div>
	);
};

export default OrderFulfillmentBlock;
